import { Alert, Slider } from '@chronosphereio/chrono-ui';
import { useFeatureFlags } from '@chronosphereio/core';
import { produce } from 'immer';
import { useMemo } from 'react';
import { useChartsTheme } from '@/components';
import {
  DEFAULT_VISUAL_DENSITY,
  StatusHistoryColorMode,
  StatusHistoryColorModes,
  StatusHistoryLabelGrouping,
  StatusHistoryPanelSpec,
  StatusHistoryStepOption,
  StatusHistoryThresholdOptions,
  UNIT_SETTINGS_NAME,
} from '@/core';
import {
  OptionsEditorColumn,
  OptionsEditorControl,
  OptionsEditorGrid,
  OptionsEditorGroup,
  SettingsAutocomplete,
  SettingsAutocompleteOption,
  UnitSelector,
  UnitSelectorProps,
} from '@/plugin-editor-components';
import {
  getRandomColor,
  ThresholdsEditorProps,
} from '@/plugin-editor-components/components/ThresholdsEditor/NewThresholdsEditor';
import { mergeTimeSeries, OptionsEditorProps, useDataQueryResult } from '@/plugin-system';
import { LabelColorMappingsEditor } from './LabelColorMappingsEditor';
import { StatusHistoryThresholdsEditor } from './StatusHistoryThresholdsEditor';
import { useResolvedLabelColorMappings } from './utils';

type ColorModeOption = SettingsAutocompleteOption & { id: StatusHistoryColorMode };

const COLOR_MODE_OPTIONS: ColorModeOption[] = [
  { id: StatusHistoryColorModes.ByValue, label: 'Color by value' },
  { id: StatusHistoryColorModes.ByLabel, label: 'Color by label' },
];

const STATE_LIKE_LABEL_NAMES = new Set([
  'phase',
  'state',
  'status',
  'condition',
  'health',
  'severity',
  'level',
  'tier',
]);

function getLabelKeyOptions(allSeries: Array<{ labels?: Record<string, string> }>): SettingsAutocompleteOption[] {
  const keys = new Set<string>();
  for (const s of allSeries) {
    if (s.labels) {
      for (const key of Object.keys(s.labels)) {
        keys.add(key);
      }
    }
  }
  return Array.from(keys)
    .sort()
    .map((key) => ({ id: key }));
}

/**
 * Infers which label key should be `color_by` (the state dimension) using two
 * signals:
 *
 * 1. Well-known state label names (phase, state, status, etc.) are preferred.
 * 2. If no well-known name is found, the label with the fewest unique values
 *    (lowest cardinality) is chosen, since state dimensions typically have
 *    fewer distinct values than entity dimensions.
 *
 * Row grouping is inferred at runtime from all labels except `color_by`.
 */
function inferColorByDefault(
  labelKeyOptions: SettingsAutocompleteOption[],
  allSeries: Array<{ labels?: Record<string, string> }>
): { color_by: string } | undefined {
  if (labelKeyOptions.length < 2) return undefined;

  const keys = labelKeyOptions.map((o) => o.id);

  const stateLike = keys.find((k) => STATE_LIKE_LABEL_NAMES.has(k.toLowerCase()));
  if (stateLike) {
    return { color_by: stateLike };
  }

  const cardinality = new Map<string, Set<string>>();
  for (const k of keys) {
    cardinality.set(k, new Set());
  }
  for (const s of allSeries) {
    if (s.labels) {
      for (const [k, v] of Object.entries(s.labels)) {
        cardinality.get(k)?.add(v);
      }
    }
  }

  const sorted = [...keys].sort((a, b) => (cardinality.get(a)?.size ?? 0) - (cardinality.get(b)?.size ?? 0));

  return { color_by: sorted[0]! };
}

export const StatusHistoryOptionsEditor = ({ value, onChange }: OptionsEditorProps<StatusHistoryPanelSpec>) => {
  const { enableStatusHistoryColorMode } = useFeatureFlags();
  const colorMode = value.color_mode ?? StatusHistoryColorModes.ByValue;
  const isColorByLabelActive = enableStatusHistoryColorMode && colorMode === StatusHistoryColorModes.ByLabel;
  const unit = value.unit ?? { kind: 'Decimal' };
  const thresholds = value.thresholds ?? {};
  const density = value.visual?.density ?? DEFAULT_VISUAL_DENSITY;
  const chartsTheme = useChartsTheme();
  const { queryResults } = useDataQueryResult({ dataTypeFilter: 'TimeSeries' });
  const allSeries = useMemo(() => mergeTimeSeries(queryResults), [queryResults]);
  const labelKeyOptions = useMemo(() => getLabelKeyOptions(allSeries), [allSeries]);

  const { resolvedLabelGrouping } = useResolvedLabelColorMappings({
    labelGrouping: value.label_grouping,
    allSeries,
    colorByLabelActive: isColorByLabelActive,
  });

  const handleColorModeChange = (_: unknown, selected: ColorModeOption | null) => {
    const newMode = selected?.id ?? StatusHistoryColorModes.ByValue;
    onChange(
      produce(value, (draft: StatusHistoryPanelSpec) => {
        draft.color_mode = newMode;
        if (newMode === StatusHistoryColorModes.ByValue) {
          delete draft.label_grouping;
        } else {
          draft.thresholds = {};
          if (!draft.label_grouping) {
            const defaults = inferColorByDefault(labelKeyOptions, allSeries);
            if (defaults) {
              draft.label_grouping = defaults;
            }
          }
        }
      })
    );
  };

  const handleThresholdsChange: ThresholdsEditorProps<
    StatusHistoryStepOption,
    StatusHistoryThresholdOptions
  >['onChange'] = (newThresholds) => {
    onChange(
      produce(value, (draft: StatusHistoryPanelSpec) => {
        draft.thresholds = {
          ...newThresholds,
          ...(newThresholds.base
            ? {
                base: {
                  ...newThresholds.base,
                  color: newThresholds.base.color ?? chartsTheme.thresholds.defaultColor,
                },
              }
            : {}),
          ...(newThresholds.steps
            ? {
                steps: newThresholds.steps.map((step, idx) => {
                  return { ...step, color: step.color ?? chartsTheme.thresholds.palette[idx] ?? getRandomColor() };
                }),
              }
            : {}),
        };
      })
    );
  };

  const handleUnitChange: UnitSelectorProps['onChange'] = (newUnit) => {
    onChange(
      produce(value, (draft: StatusHistoryPanelSpec) => {
        draft.unit = newUnit;
      })
    );
  };

  const handleVisualDensityChange = (_: Event, sliderValue: number | number[]) => {
    const newValue = Array.isArray(sliderValue) ? sliderValue[0] : sliderValue;
    if (newValue !== undefined) {
      onChange(
        produce(value, (draft: StatusHistoryPanelSpec) => {
          draft.visual = {
            density: newValue,
          };
        })
      );
    }
  };

  const handleColorByChange = (_: unknown, selected: SettingsAutocompleteOption | null) => {
    const colorBy = selected?.id ?? '';
    onChange(
      produce(value, (draft: StatusHistoryPanelSpec) => {
        draft.label_grouping = { color_by: colorBy };
      })
    );
  };

  const handleLabelGroupingChange = (updated: StatusHistoryLabelGrouping) => {
    onChange(
      produce(value, (draft: StatusHistoryPanelSpec) => {
        draft.label_grouping = updated;
      })
    );
  };

  return (
    <OptionsEditorGrid>
      <OptionsEditorColumn>
        <OptionsEditorGroup title="Visual">
          <OptionsEditorControl
            label={'Density'}
            description="Changes the vertical density of the rows. Modify the resolution in the Query tab to change horizontal density."
            control={
              <Slider
                data-testid={'visualDensity'}
                value={density}
                valueLabelDisplay="auto"
                marks
                min={0}
                max={10}
                onChange={handleVisualDensityChange}
              />
            }
          />
        </OptionsEditorGroup>
      </OptionsEditorColumn>
      <OptionsEditorColumn>
        {enableStatusHistoryColorMode && (
          <OptionsEditorGroup title="Color Mode">
            <OptionsEditorControl
              label="Mode"
              description="Choose how cell colors are determined"
              control={
                <SettingsAutocomplete
                  options={COLOR_MODE_OPTIONS}
                  value={COLOR_MODE_OPTIONS.find((o) => o.id === colorMode) ?? COLOR_MODE_OPTIONS[0]!}
                  onChange={handleColorModeChange}
                  disableClearable
                />
              }
            />
          </OptionsEditorGroup>
        )}
        {isColorByLabelActive && (
          <OptionsEditorGroup title="Label Grouping">
            {labelKeyOptions.length < 2 && (
              <Alert severity="warning">
                {allSeries.length === 0
                  ? 'Run the query to discover available label keys.'
                  : 'This mode works best with queries that produce multiple series per entity (e.g. kube_pod_status_phase). The query needs at least two label keys so one can determine state colors.'}
              </Alert>
            )}
            <OptionsEditorControl
              label="Color by"
              description="Each unique value of this label becomes a distinct state and color (e.g. phase, status). Rows are grouped automatically by all other labels."
              control={
                <SettingsAutocomplete
                  options={labelKeyOptions}
                  value={labelKeyOptions.find((o) => o.id === value.label_grouping?.color_by) ?? null}
                  onChange={handleColorByChange}
                />
              }
            />
          </OptionsEditorGroup>
        )}
        {!isColorByLabelActive && (
          <OptionsEditorGroup title={UNIT_SETTINGS_NAME}>
            <UnitSelector value={unit} onChange={handleUnitChange} />
          </OptionsEditorGroup>
        )}
      </OptionsEditorColumn>
      <OptionsEditorColumn>
        {isColorByLabelActive && value.label_grouping?.color_by && (
          <LabelColorMappingsEditor
            colorByLabel={value.label_grouping.color_by}
            labelGrouping={resolvedLabelGrouping ?? value.label_grouping}
            onChange={handleLabelGroupingChange}
          />
        )}
        {!isColorByLabelActive && (
          <StatusHistoryThresholdsEditor hideThresholdMode thresholds={thresholds} onChange={handleThresholdsChange} />
        )}
      </OptionsEditorColumn>
    </OptionsEditorGrid>
  );
};
